/**
 * External dependencies
 */
import { useEffect, useMemo, useState } from 'react';
import useRouter from '@naruto/naruto-hooks/src/useRouter';

/**
 * Internal dependencies
 */
import { SideItemProps } from './types';

export const LIST_CHILD_PADDING = 65;

interface UseData {
  /* Data : General */
  selected: boolean;
  parentSelected: boolean;
  renderChild: boolean;
  collapse: boolean;
  /* Functions: General */
  onToggleCollapse: (event: React.MouseEvent) => void;
  onSelectRoute: (event: React.MouseEvent) => void;
}

function useData(props: SideItemProps): UseData {
  /* Global & Local State */
  const { item, parentRoute, onChangeRoute } = props;
  const { pathname, matchPath } = useRouter();
  const [collapse, setCollapse] = useState(false);

  /* Data */
  const selected = useMemo<boolean>(() => {
    const isWatchPathActive = !!matchPath(pathname, {
      path: `${parentRoute}${item.route}`,
      exact: false,
      strict: false
    });
    return !!parentRoute && isWatchPathActive;
  }, [pathname, item, parentRoute]);
  const parentSelected = useMemo<boolean>(() => {
    if (item.root) {
      return pathname === item.route;
    }
    const isWatchPathActive = !!matchPath(pathname, {
      path: `${parentRoute}${item.route}`,
      exact: false,
      strict: false
    });
    return isWatchPathActive;
  }, [pathname, item]);
  const generateParentRoute = useMemo<string>(() => `${parentRoute}${item.route}`, [item]);
  const renderChild = useMemo<boolean>(
    () => Boolean(item.subRoute && item.subRoute.filter(item => item.showInSidebar).length > 0),
    [item]
  );

  /* Functions */
  const onToggleCollapse = (event: React.MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    setCollapse(!collapse);
  };
  const onSelectRoute = (event: React.MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    onChangeRoute(generateParentRoute);
  };

  /* Hooks */
  useEffect(() => {
    if (parentSelected) {
      setCollapse(true);
    }
  }, [parentSelected]);

  /* Main */
  return {
    /* Data : General */
    selected,
    parentSelected,
    renderChild,
    collapse,
    /* Functions : General */
    onToggleCollapse,
    onSelectRoute
  };
}

export default useData;
